import "./game-screen.css";
import type { ParentActions, ParentView } from "./view-model";

export interface ParentScreenProps {
  view: ParentView;
  actions: ParentActions;
}

export function ParentScreen({ view, actions }: ParentScreenProps) {
  return (
    <main
      className="page parent-page"
      data-testid="parent-page"
      data-status={view.status}
    >
      <div className="dev">
        <header className="head">
          <h1 className="logo" aria-label="zookeepbit" style={{ margin: 0 }}>
            <b>ZOO</b>
            <b>KEEP</b>
            <b>BIT</b>
          </h1>
        </header>

        <div className="panel parent-card" data-testid="parent-card">
          {view.status === "checking" && (
            <div role="status" className="note" style={{ fontSize: "14px" }}>
              Checking the link…
            </div>
          )}

          {view.status === "approve" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>A young player asked for your OK.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
                If you are their parent or carer, you can say OK so they can buy things in zookeepbit. You sign in with your own Google account. We don't see your email or name, and we don't show who asked.
              </p>
              <button
                className="btn"
                type="button"
                data-testid="parent-continue"
                onClick={() => actions.onContinueWithGoogle()}
              >
                Sign in with Google to say OK
              </button>
              <small className="note" style={{ display: "block" }}>
                This shows that an adult is in charge. It is not a legal age check.
              </small>
            </div>
          )}

          {view.status === "withdraw" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>Take back your OK?</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
                If you say yes, the player can no longer buy things until a parent says OK again. Use the same Google account you used before.
              </p>
              <button
                className="btn"
                type="button"
                data-testid="parent-continue"
                onClick={() => actions.onContinueWithGoogle()}
              >
                Sign in with Google to take back my OK
              </button>
            </div>
          )}

          {view.status === "starting" && (
            <div role="status" className="note" style={{ fontSize: "14px" }}>
              Taking you to Google…
            </div>
          )}

          {view.status === "approved" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>Thank you. The OK is saved.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px" }}>
                You can close this page.
              </p>
            </div>
          )}

          {view.status === "withdrawn" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>Your OK was taken back.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px" }}>
                You can close this page.
              </p>
            </div>
          )}

          {view.status === "error" && (
            <div role="alert" style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>That didn't work.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
                Use the link again, with a different Google account if you used the player's own. If it still fails, ask the player for a new link.
              </p>
            </div>
          )}

          {view.status === "invalid" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>This link has run out.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
                Links work for 30 minutes and only once. Ask the player to make a new one.
              </p>
            </div>
          )}

          {view.status === "offline" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>Can't reach zookeepbit.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px" }}>
                Check your connection and try again.
              </p>
              <button
                className="btn"
                type="button"
                data-testid="parent-retry"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.location.reload();
                  }
                }}
              >
                Try again
              </button>
            </div>
          )}

          {view.status === "unavailable" && (
            <div style={{ display: "grid", gap: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "16px" }}>Signing in with Google isn't available right now.</h3>
              <p className="note" style={{ margin: 0, fontSize: "14px" }}>
                Please try again later.
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
